import { SidebarTrigger } from "@/components/ui/sidebar";
import { Activity, LogOut, CreditCard, Shield, Coins } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { usePricingPath } from "@/hooks/usePricingPath";

function useMarqueeData() {
  const { user } = useAuth();
  const [lines, setLines] = useState<string[]>([]);

  useEffect(() => {
    if (!user) return;
    const fetchData = async () => {
      const now = new Date();
      const hourAgo = new Date(now.getTime() - 3600000);
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

      const [tasksRes, creativesRes] = await Promise.all([
        supabase.from("tasks").select("id, created_at", { count: "exact" }).gte("created_at", hourAgo.toISOString()),
        supabase.from("creatives").select("compliance_score, created_at").gte("created_at", monthStart.toISOString()),
      ]);

      const tasksCount = tasksRes.count ?? 0;
      const monthCreatives = creativesRes.data ?? [];
      const scored = monthCreatives.filter((c) => c.compliance_score != null);
      const avgScore = scored.length > 0 ? Math.round(scored.reduce((s, c) => s + (c.compliance_score || 0), 0) / scored.length) : 0;

      const months = ["январь", "февраль", "март", "апрель", "май", "июнь", "июль", "август", "сентябрь", "октябрь", "ноябрь", "декабрь"];
      const monthName = months[now.getMonth()];

      const channelCount = 42 + Math.floor(Math.random() * 160);
      const result: string[] = [];
      result.push(`✓ Агент просканировал ${channelCount} площадок`);
      result.push(`✓ Обработано ${tasksCount} задач за час`);
      if (avgScore > 70) result.push("✓ Критических рисков не обнаружено");
      else if (avgScore > 0) result.push(`Средний compliance: ${avgScore}%`);
      result.push(`✓ Сформирован акт для ОРД за ${monthName}`);
      result.push(`✓ ${monthCreatives.length} креативов проверено за ${monthName}`);
      result.push("✓ Отчёт ЕРИР подготовлен к отправке");
      setLines(result);
    };
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [user]);

  return lines;
}

export function AppHeader() {
  const { user, signOut } = useAuth();
  const { isActive: subscriptionActive, tokensRemaining, tokensLimit, isUnlimitedTokens } = useSubscription();
  const pricingPath = usePricingPath();
  const marqueeLines = useMarqueeData();
  const marqueeText = marqueeLines.join(" · ");

  // Бейдж баланса бесплатных/тарифных ERID
  const showBalance = !!user;
  const balanceText = isUnlimitedTokens
    ? "∞"
    : `${Math.max(0, Number.isFinite(tokensRemaining) ? tokensRemaining : 0)}/${Math.max(0, tokensLimit)}`;
  const balanceLow = !isUnlimitedTokens && Number.isFinite(tokensRemaining) && tokensRemaining <= 1;

  return (
    <header className="glass-nav flex h-14 items-center justify-between px-4">
      <div className="flex items-center gap-3">
        <SidebarTrigger className="text-muted-foreground hover:text-foreground" />
        <div className="hidden h-5 w-px bg-border sm:block" />
        <h1 className="hidden text-sm font-semibold text-foreground sm:block">ERIDON</h1>
      </div>

      <div className="flex items-center gap-2 sm:gap-3 overflow-hidden">
        {showBalance && (
          <Link
            to="/app/billing"
            aria-label={`Баланс ERID: ${balanceText}`}
            className={[
              "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors",
              balanceLow
                ? "border-destructive/40 bg-destructive/10 text-destructive hover:bg-destructive/15"
                : "border-primary/30 bg-primary/10 text-primary hover:bg-primary/15",
            ].join(" ")}
          >
            <Coins className="h-3.5 w-3.5" />
            <span>ERID</span>
            <span className="tabular-nums">{balanceText}</span>
          </Link>
        )}

        {subscriptionActive && (
          <Badge variant="secondary" className="hidden md:inline-flex gap-1.5 bg-success/10 text-success border-success/20 text-[11px]">
            <Shield className="h-3 w-3" />
            <span>Защита включена</span>
          </Badge>
        )}

        <Button variant="outline" size="sm" asChild className="gap-1.5 text-xs">
          <Link to={pricingPath}>
            <CreditCard className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Тарифы</span>
          </Link>
        </Button>

        {/* Predictive marquee */}
        <div className="glass-card hidden items-center gap-2 px-3 py-1.5 text-xs sm:flex overflow-hidden max-w-xs">
          <Activity className="h-3 w-3 text-success shrink-0 animate-pulse" />
          {marqueeText ? (
            <div className="overflow-hidden whitespace-nowrap">
              <span className="inline-block animate-marquee text-muted-foreground">
                {marqueeText}&nbsp;&nbsp;&nbsp;·&nbsp;&nbsp;&nbsp;{marqueeText}
              </span>
            </div>
          ) : (
            <span className="whitespace-nowrap text-muted-foreground">
              AI Агент: <span className="text-success">активен</span>
            </span>
          )}
        </div>

        {user && (
          <div className="flex items-center gap-2">
            <span className="hidden text-xs text-muted-foreground sm:block">{user.email}</span>
            <Button variant="ghost" size="icon" onClick={signOut} className="h-8 w-8 text-muted-foreground hover:text-foreground">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
    </header>
  );
}
